import { Observer } from 'mobx-react-lite'
import QueryWaiter from 'components/common/QueryWaiter'
import Modals from 'singletons/Modals'

import ExclamationIcon from '@ui-components-3/icons/lib/regular/CircleExclamation'
import ExclamationCheckIcon from '@ui-components-3/icons/lib/regular/CircleExclamationCheck'
import LanguageIcon from '@ui-components-3/icons/lib/regular/Language'
import Tooltip from '@ui-components-3/ui/lib/components/Tooltip'
import User from 'singletons/User'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import { useState } from 'react'
import CrisisEscalationModal from './CrisisEscalationModal'
import PhoneCall from 'components/Dashboard/Main/Visit/VisitPanel/common/PhoneCall'
import TestIds from 'constants/test-ids'
import { useSelectCrisisStream } from 'hooks/crisis/useCrisisStream'
import PendingQueries from 'singletons/PendingQueries'

const Q = ['getStartVideoCallV2', 'postConference', '#startVideo']

// TODO - improve store type
const getSubmitData = (store: any, crisisStreamApi: ReturnType<typeof useSelectCrisisStream>) => {
  const visit = store.visit
  const isSuccess = visit.isSuccess

  const type = isSuccess ? 'session' : 'visit'

  switch (visit.modality) {
    case 'video':
      if (!store.video.active && visit.canStartVideoCall) {
        return {
          testId: visit.started ? TestIds.visitButtons.continue : TestIds.visitButtons.start,
          onSubmit: () => store.startVideo(),
          submitText: visit.started ? `Continue ${type}` : `Start ${type}`,
        }
      } else if (visit.started) {
        return {
          testId: TestIds.visitButtons.finish,
          onSubmit: () => {
            store.stopVideo()
            Modals.open('patientSummary', { visit })
          },
          submitText: `Finish ${type}`,
        }
      }
      return {
        onSubmit: undefined,
        submitText: '',
      }

    case 'phone': {
      const { isCrisis, shouldVerifyPhone, isProviderConnected } = crisisStreamApi || {}

      const startCrisisVisit = () => visit?.start?.()
      const startRegularPhoneVisit = () => visit?.phoneCall?.start?.()

      const isDisabled = isCrisis && (!!shouldVerifyPhone || !isProviderConnected)

      return visit.started
        ? {
            testId: TestIds.visitButtons.finish,
            onSubmit: () => Modals.open('patientSummary', { visit }),
            submitText: `Finish ${type}`,
          }
        : {
            testId: TestIds.visitButtons.start,
            onSubmit: isCrisis ? startCrisisVisit : startRegularPhoneVisit,
            submitText: `Start ${type}`,
            disabled: isDisabled,
          }
    }
    case 'chat':
      return visit.started
        ? {
            testId: TestIds.visitButtons.finish,
            onSubmit: () => Modals.open('patientSummary', { visit }),
            submitText: 'Finish visit',
          }
        : {
            testId: TestIds.visitButtons.start,
            onSubmit: () => store.startChat(),
            submitText: 'Start visit',
          }
    default:
      throw new Error(`Bad visit modality: ${visit.modality}`)
  }
}

type ActionsProps = {
  // TODO - add type for store
  store: any
}

const Actions = ({ store }: ActionsProps) => {
  const isSuccess = store.visit.isSuccess
  const [crisisEscalationModalOpened, setCrisisEscalationModalOpened] = useState(false)

  const crisisStreamApi = useSelectCrisisStream()

  const { callLanguageLine, isCrisisLanguageLineConnected, isCrisis } = crisisStreamApi

  return (
    <>
      <div className="flex items-center gap-4 flex-wrap md:flex-nowrap pr-4">
        <Observer>
          {() => {
            if (!store.visit.visitId || !User.getFeatureFlagByName('pwCrisisEscalation')) {
              return null
            }

            const isLoadingEscalations =
              PendingQueries.some('postVisitEscalation') || PendingQueries.some('getVisitEscalations')

            const label = store.visit.crisisEscalation?.lastEscalation
              ? 'The Crisis Response Team has been notified'
              : 'Click here to escalate to the on-call Crisis Response Team'

            return (
              <Tooltip placement="top" content={<span className={'text-sm leading-6'}>{label}</span>} delay={100}>
                <button
                  disabled={!!store.visit.crisisEscalation?.lastEscalation || isLoadingEscalations}
                  onClick={() => setCrisisEscalationModalOpened(true)}
                  // TODO: Replace with fuchsia primary button from component library
                  className={classNames(
                    'relative btn btn-sm btn-neutral h-10 md:h-12 w-10 md:w-12 rounded-[10px] shadow-md uppercase text-base font-[600]',
                    'relative btn btn-sm btn-fuchsia-outlined h-10 md:h-12 w-10 md:w-12 text-fuchsia rounded-[10px] shadow-md uppercase text-base font-[600]',
                    'hover:bg-fuchsia-100 hover:shadow-lg transition-all duration-300',
                  )}
                  aria-label={label}
                >
                  {store.visit.crisisEscalation?.lastEscalation ? (
                    <ExclamationCheckIcon className={'typography-neutral-800 text-lg'} aria-hidden="true" />
                  ) : (
                    <ExclamationIcon aria-hidden="true" />
                  )}
                </button>
              </Tooltip>
            )
          }}
        </Observer>
        <Observer>
          {() => {
            if (store.visit?.modality === 'chat' || isCrisis) {
              return null
            }

            return <PhoneCall visit={store.visit} />
          }}
        </Observer>
        <Observer>
          {() =>
            !!store.visit?.isCrisis &&
            !!store.visit.started && (
              <Tooltip
                placement="bottom"
                content={isCrisisLanguageLineConnected ? 'Language Line is on call' : 'Call Language Line'}
              >
                <button
                  type="button"
                  className="btn-md md:btn-lg btn-neutral w-10 md:w-12 min-w-0"
                  aria-label="Call Language Line"
                  onClick={() => callLanguageLine()}
                  disabled={!!isCrisisLanguageLineConnected}
                >
                  <LanguageIcon aria-hidden="true" />
                </button>
              </Tooltip>
            )
          }
        </Observer>
        <Observer>
          {() => {
            const cancelText = store.visit.started ? `Cancel ${isSuccess ? 'session' : 'visit'}` : ''
            const onCancel = store.visit.started ? () => Modals.open('cancelVisit', { visit: store.visit }) : undefined
            return (
              !!cancelText &&
              !!store.visit.isCancelPossible && (
                <button
                  type="button"
                  className="btn-md md:btn-lg btn-fuchsia-outlined whitespace-nowrap"
                  id={TestIds.visitButtons.cancel}
                  onClick={onCancel}
                  disabled={store.visit.isMemberLocked}
                >
                  {cancelText}
                </button>
              )
            )
          }}
        </Observer>

        <Observer>
          {() => {
            const { submitText, onSubmit, testId, disabled } = getSubmitData(store, crisisStreamApi)
            const { isCrisis, isProviderConnected } = crisisStreamApi || {}

            const isButtonDisabled =
              store.visit.isMemberLocked ||
              store.disableStart ||
              store.video.active ||
              (store.visit.modality === 'chat' && store.visit.status === 'in-progress') ||
              (store.visit.started ? false : store.visit.shouldDisableStarting) ||
              !!disabled

            // Show tooltip for crisis visits when provider hasn't connected
            const shouldShowTooltip = isCrisis && !isProviderConnected && !store.visit.started
            const tooltipContent = 'Please connect to the phone call before starting this visit'

            /*
            TODO: simplify disabled logics; should be just status === 'in-progress' or so
          */
            const button = !!submitText && (
              <button
                type="button"
                className="btn-md md:btn-lg btn-primary whitespace-nowrap"
                data-testid={testId}
                disabled={isButtonDisabled}
                onClick={onSubmit}
              >
                <QueryWaiter query={Q} />
                {submitText}
              </button>
            )

            return shouldShowTooltip ? (
              <Tooltip
                placement="top"
                content={<span className="text-sm leading-6">{tooltipContent}</span>}
                delay={100}
              >
                {button}
              </Tooltip>
            ) : (
              button
            )
          }}
        </Observer>
      </div>
      <Observer>
        {() => (
          <CrisisEscalationModal
            opened={crisisEscalationModalOpened}
            visit={store.visit}
            onClose={() => setCrisisEscalationModalOpened(false)}
          />
        )}
      </Observer>
    </>
  )
}

export default Actions
